/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ArrowLeft, ShieldCheck, KeyRound, Lock, AlertTriangle, Inbox, Eye } from "lucide-react";

// v77.99.74 — Public security page.
//
// Companion to /.well-known/security.txt (RFC 9116). Where the .txt
// is for automated tooling and security researchers, this page is the
// human-readable equivalent that lives in the same footer as Privacy
// and Terms.
//
// Tone: calm, professional, plain English. No jargon for jargon's
// sake — but precise enough to be useful to a B2B prospect's
// security questionnaire or an auditor's scope-check.

const Section = ({ title, id, icon: Icon, children }) => (
  <section className="mt-10" id={id} data-testid={`security-section-${id}`}>
    <h2 className="font-heading text-xl font-medium text-[#2E3A3F] inline-flex items-center gap-2.5">
      {Icon && (
        <span className="w-8 h-8 rounded-sm bg-[#E0F2F2] text-[#116B6E] inline-flex items-center justify-center">
          <Icon className="w-4 h-4" strokeWidth={1.6} />
        </span>
      )}
      {title}
    </h2>
    <div className="mt-3 space-y-3 text-[#2E3A3F] leading-relaxed">{children}</div>
  </section>
);

export default function Security() {
  return (
    <div className="min-h-screen bg-[#F5F7F8] text-[#2E3A3F]">
      <header className="border-b border-[#D9E1E3] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="security-brand">
            <Logo size={32} />
            <span className="font-heading font-semibold text-lg">CoParent Console</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#6F828A] hover:text-[#1F9FA4] inline-flex items-center gap-1"
            data-testid="security-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="security-page">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Trust &amp; safety</div>
          <h1 className="font-heading text-4xl font-semibold mt-2">Security at CoParent Console</h1>
          <p className="text-[#6F828A] mt-2 text-base leading-relaxed">
            Our users entrust us with deeply personal records — court bundles,
            children's schedules, mediator correspondence. This page sets out
            how we protect that information, what our scope is, and how to
            report a vulnerability if you find one.
          </p>
          <p className="text-sm text-[#6F828A] mt-2" data-testid="security-last-updated">
            Last updated: 28 February 2026
          </p>
        </div>

        <Section title="How to report a vulnerability" id="report" icon={Inbox}>
          <p>
            If you've found a security issue in CoParent Console, please email{" "}
            <a
              href="mailto:security@coparentconsole.com?subject=Security%20report"
              className="text-[#1F9FA4] hover:text-[#178085]"
              data-testid="security-contact-email"
            >
              security@coparentconsole.com
            </a>{" "}
            with the subject line &quot;Security report&quot;. Please give us
            time to investigate and remediate before any public disclosure —
            we'll keep you in the loop the whole way.
          </p>
          <p>
            Our machine-readable contact information is published at{" "}
            <a
              href="/.well-known/security.txt"
              className="text-[#1F9FA4] hover:text-[#178085]"
              data-testid="security-txt-link"
            >
              /.well-known/security.txt
            </a>{" "}
            following <span className="font-mono-data text-sm">RFC&nbsp;9116</span>.
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-[#3F595B]">
            <li>We acknowledge new reports within <strong>2 business days</strong>.</li>
            <li>We aim to triage and provide an initial assessment within <strong>5 business days</strong>.</li>
            <li>High-severity issues are fixed and deployed as a priority; expected timelines are shared with the reporter.</li>
            <li>We do not currently run a paid bug-bounty programme but we always credit researchers who request acknowledgment.</li>
          </ul>
        </Section>

        <Section title="What we protect" id="scope" icon={ShieldCheck}>
          <p>
            CoParent Console holds household records for separated and divorced
            parents: messages, calendar events, expense ledgers, maintenance
            schedules, journal entries, document uploads, child profiles, and
            mediator/solicitor access grants. We treat all of these as
            sensitive personal data.
          </p>
          <p>
            <strong>In scope</strong> for vulnerability reports:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-[#3F595B]">
            <li>The production application at <span className="font-mono-data">app.coparentconsole.com</span> and our marketing site at <span className="font-mono-data">coparentconsole.com</span>.</li>
            <li>The CoParent Console API.</li>
            <li>Authentication, session management, and access control (including the third-party / mediator grant flow).</li>
            <li>Data-handling endpoints — anything that reads, writes, exports, or shares user records.</li>
          </ul>
          <p>
            <strong>Out of scope</strong>:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-[#3F595B]">
            <li>Denial-of-service or rate-limit research that affects other customers.</li>
            <li>Findings that depend on root-level device compromise, stolen credentials, or social engineering of our staff.</li>
            <li>Issues in third-party services we integrate with (Stripe, Resend, Cloudflare Turnstile) — please report those upstream.</li>
            <li>Findings in preview / staging URLs (e.g. <span className="font-mono-data">*.preview.emergentagent.com</span>).</li>
          </ul>
        </Section>

        <Section title="How we protect data in transit and at rest" id="protection" icon={Lock}>
          <p>
            All traffic to and from CoParent Console is encrypted in transit
            with TLS. Stored data sits on managed infrastructure with
            encryption at rest enabled by default.
          </p>
          <p>
            Backups run automatically and are encrypted. We retain personal
            data only as long as it serves the purposes set out in our{" "}
            <Link to="/privacy" className="text-[#1F9FA4] hover:text-[#178085]">
              Privacy Policy
            </Link>{" "}
            or as required by law.
          </p>
        </Section>

        <Section title="Authentication and session security" id="auth" icon={KeyRound}>
          <ul className="list-disc pl-5 space-y-1.5 text-[#3F595B]">
            <li>Passwords are hashed with a modern adaptive hash (bcrypt). We never store passwords in plain text.</li>
            <li>Sessions ride in an <strong>HttpOnly, Secure, SameSite=Lax cookie</strong>. JavaScript cannot read the session token, so cross-site scripting cannot exfiltrate it.</li>
            <li>State-changing requests are protected by a <strong>double-submit CSRF token</strong> with timing-safe comparison.</li>
            <li>Cross-origin requests are restricted to an explicit allow-list of our own domains — no wildcards with credentials.</li>
            <li>Optional two-factor authentication (TOTP) is available for any account; we strongly encourage it for users who share their device or have a high-conflict co-parent.</li>
            <li>Sessions can be reviewed and revoked individually from <em>Settings &rarr; Security</em>.</li>
            <li>Sign-up and password reset are gated by Cloudflare Turnstile to deter credential-stuffing.</li>
          </ul>
        </Section>

        <Section title="What we'll never ask you for" id="never" icon={Eye}>
          <ul className="list-disc pl-5 space-y-1.5 text-[#3F595B]">
            <li>Your CoParent Console password — including in any email, chat, or phone call.</li>
            <li>Remote access to your device.</li>
            <li>Sensitive financial details outside our Stripe payment flow (Stripe handles card data; we never see it).</li>
            <li>One-time codes generated by your authenticator app or sent to you by us.</li>
          </ul>
          <p>
            If anyone claiming to be from CoParent Console asks for any of the
            above, please ignore the message and report it to{" "}
            <a
              href="mailto:security@coparentconsole.com?subject=Suspicious%20message"
              className="text-[#1F9FA4] hover:text-[#178085]"
            >
              security@coparentconsole.com
            </a>.
          </p>
        </Section>

        <Section title="Responsible disclosure expectations" id="responsible-disclosure" icon={AlertTriangle}>
          <p>
            We greatly appreciate the work of security researchers. To keep
            our users safe while we investigate, please:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-[#3F595B]">
            <li>Give us a reasonable period (typically 90 days) to remediate before any public disclosure.</li>
            <li>Avoid accessing, modifying, or destroying user data — confirm impact with the minimum interaction needed.</li>
            <li>Never run automated tooling that floods the service or could affect another customer's account.</li>
            <li>If you accidentally access data that isn't yours, stop immediately and let us know.</li>
          </ul>
          <p>
            In exchange, we commit to acting in good faith: keeping you
            informed, crediting your work when you want us to, and never
            taking legal action against researchers who follow these
            guidelines.
          </p>
        </Section>

        <Section title="Audit and compliance posture" id="compliance">
          <p>
            CoParent Console is operated from Ireland and processes personal
            data under the EU GDPR and the Irish Data Protection Acts 2018.
            Our handling, subject rights, and breach-response procedures are
            documented in the{" "}
            <Link to="/privacy" className="text-[#1F9FA4] hover:text-[#178085]">
              Privacy Policy
            </Link>.
          </p>
          <p>
            We are happy to share additional security documentation
            (architecture diagrams, sub-processor list, breach-response
            playbook) with B2B prospects who request it as part of vendor
            due-diligence. Please get in touch at{" "}
            <a
              href="mailto:security@coparentconsole.com?subject=Security%20questionnaire"
              className="text-[#1F9FA4] hover:text-[#178085]"
              data-testid="security-questionnaire-email"
            >
              security@coparentconsole.com
            </a>{" "}
            and we'll route it to the right person.
          </p>
        </Section>

        <div className="mt-14 pt-6 border-t border-[#D9E1E3] text-sm text-[#6F828A] flex flex-wrap items-center gap-x-3 gap-y-1">
          <Link to="/" className="hover:text-[#1F9FA4]">Home</Link>
          <span aria-hidden="true">·</span>
          <Link to="/privacy" className="hover:text-[#1F9FA4]">Privacy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms" className="hover:text-[#1F9FA4]">Terms</Link>
          <span aria-hidden="true">·</span>
          <Link to="/contact" className="hover:text-[#1F9FA4]">Contact</Link>
        </div>
      </main>
    </div>
  );
}
